import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestDrop = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    brand: '',
    items: '',
    email: '',
    notes: '',
    terms_accepted: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pre-fill email from logged in user
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.terms_accepted) {
      toast.error('You must accept the Terms & Conditions to request a drop');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/request-drop`, formData);
      toast.success('Request submitted! Check your email for confirmation.');
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9FB]" data-testid="request-drop-page">
      <nav className="border-b border-stone-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-playfair font-bold text-primary tracking-tight">
            Sharedequal
          </Link>
          <Link to="/">
            <Button variant="ghost" className="rounded-sm">Back to Home</Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-playfair font-bold text-primary tracking-tight" data-testid="request-heading">Request a Drop</h1>
          <p className="text-muted-foreground mt-2">Tell us what you'd like to order and we'll organize a group drop</p>
        </div>

        {!submitted ? (
          <Card className="border-stone-200 rounded-sm">
            <CardHeader>
              <CardTitle>Express Your Interest</CardTitle>
              <CardDescription>
                Tell us about the brand or items you'd like. This is a preliminary interest form — no commitment required.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Expectation Note */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>How it works:</strong> This form collects your initial interest. Our team will reach out via WhatsApp or email to discuss specifics, gather more details, and assess feasibility before organizing the drop.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand or Product *</Label>
                  <Input
                    id="brand"
                    data-testid="request-brand-input"
                    placeholder="e.g., La Martina, Adolfo Cambiaso, Casablanca"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    required
                    className="rounded-sm border-stone-300 focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="items">What are you looking for?</Label>
                  <Textarea
                    id="items"
                    data-testid="request-items-input"
                    placeholder="e.g., Mallets, boots, polo jerseys, helmets... (general description is fine)"
                    value={formData.items}
                    onChange={(e) => setFormData({ ...formData, items: e.target.value })}
                    required
                    className="rounded-sm border-stone-300 focus:ring-1 focus:ring-primary min-h-[100px]"
                  />
                  <p className="text-xs text-gray-500">No need for exact specifications yet — we'll coordinate details later.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Contact Email *</Label>
                  <Input
                    id="email"
                    data-testid="request-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="rounded-sm border-stone-300 focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="request-notes-input"
                    placeholder="Any preferences, timeline, or questions?"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="rounded-sm border-stone-300 focus:ring-1 focus:ring-primary min-h-[80px]"
                  />
                </div>

                {/* Terms Acceptance */}
                <div className="flex items-start space-x-3 p-4 bg-stone-50 border border-stone-200 rounded-lg">
                  <Checkbox
                    id="terms-accepted"
                    data-testid="terms-accepted-checkbox"
                    checked={formData.terms_accepted}
                    onCheckedChange={(checked) => setFormData({ ...formData, terms_accepted: checked })}
                    className="mt-0.5"
                  />
                  <label htmlFor="terms-accepted" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                    I confirm I have read and agree to the{' '}
                    <Link to="/terms-and-conditions" className="text-primary hover:underline font-medium" target="_blank">
                      Terms & Conditions
                    </Link>
                  </label>
                </div>

                <Button
                  type="submit"
                  data-testid="request-submit-button"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm uppercase tracking-wide shadow-subtle"
                  disabled={loading || !formData.terms_accepted}
                >
                  {loading ? 'Submitting...' : 'Submit Interest'}
                </Button>

                <p className="text-xs text-center text-gray-500">
                  After submission, our team will contact you to discuss details. No payment required at this stage.
                </p>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-stone-200 rounded-sm">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-playfair font-bold text-primary mb-2">Request Submitted!</h2>
              <p className="text-muted-foreground mb-6">We'll review your request and be in touch within 24 hours.</p>
              <Link to="/">
                <Button className="bg-primary hover:bg-primary/90 rounded-sm">Back to Home</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RequestDrop;